import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { replace } from 'connected-react-router';
import { Query } from '@apollo/client/react/components';
import { debounce, reject, find, pick, isEqual, isArray, zipObject, map, omitBy, get } from 'lodash';
import { getRecruiter } from '@src/redux/selectors/recruiter';
import { updateCompanyProfile, updateCompanyProfileId } from '@src/redux/actions/company';
import { setErrorFlashMessage, setSuccessFlashMessage } from '@src/redux/actions/flashMessages';
import { captureError, isErrorWithCode } from '@src/util/error';
import { createLink } from '@src/util/createLink';
import { idify } from '@src/util/idify';
import apolloClient from '@src/graphql/apollo';
import { trackUpdateCompanyProfile } from '@src/metrics';
import { COMPANY_PROFILE_DICTIONARY } from '@src/metrics/metrics.enum';
import {
  CompanyByProfileDocument,
  CompanyByProfileQuery,
  CompanyByProfileQueryVariables,
  CompanyDocument,
  CompanyFundingType,
  CompanyProfileStateEnum,
  CompanyPublicProfileListDataDocument,
  CompanyPublicProfileListDataQuery,
  CompanyRevenueType,
  CompanySizeType,
  CompanyTypeType,
  PublishCompanyProfileForPublicProfileDocument,
  SaveCompanyProfileDocument,
  SessionDocument,
  UpdateCompanyDocument,
} from '@src/graphql/generated';
import { ApolloErrorCode } from '@src/enums/apolloErrorCode.enum';
import { COMPANY_PROFILE_ROUTE } from '@src/Routes/links';
import { DEFAULT_CURRENCY_ID } from '@src/constants';
import LoadError from './LoadError';
import { View } from './View';
import './styles.scss';

const FIELD_UPDATE_LIST = ['id', 'state', 'isPublished', 'isComplete', 'video', 'completeness'];
const FIELD_UPDATE_BEFORE_SEND_LIST = [
  'logo',
  'headerImage',
  'video',
  'companySize',
  'revenue',
  'productImages',
  'locations',
  'officeVideo',
  'officeImages',
  'benefits',
  'clients',
  'marketAreas',
  'marketCountries',
  'socialMedia',
  'media',
  'funding',
  'latestFunding',
];
const MISSION_STATEMENT_MIN_LENGTH = 80;

function validateForm(profile) {
  const requiredFields = ['name', 'missionStatement', 'logo', 'headerImage', 'locations', 'companySize'];

  const required = zipObject(
    requiredFields,
    map(requiredFields, field => {
      const item = profile[field];

      return !item || (isArray(item) ? !item.length : !item);
    }),
  );

  const errors = omitBy(required, v => !v);

  if (profile.missionStatement && profile.missionStatement.trim().length < MISSION_STATEMENT_MIN_LENGTH) {
    errors.missionStatement = true;
  }

  return errors;
}

function applyToData(key, value, newState) {
  return ({ profile, changes }) => ({
    changes: {
      ...changes,
      ...(FIELD_UPDATE_BEFORE_SEND_LIST.includes(key) ? transformForSave(key, value) : { [key]: value || null }),
    },
    profile: { ...profile, ...newState },
  });
}

function transformForSave(key, value) {
  switch (key) {
    case 'logo':
      return { logoId: value ? value.id : null };
    case 'headerImage':
      return { headerImageId: value ? idify(value) : null };
    case 'video':
      return { video: value.rawUrl ? value.rawUrl : null };
    case 'companySize':
      return { companySizeId: value.id };
    case 'revenue':
      return { revenueId: value.id || null };
    case 'productImages':
      return { productImageIds: [...value.filter(item => !!item).map(item => idify(item))] };
    case 'locations':
      return {
        locationIds: value.filter(item => !!item).map(item => item.id),
        offices: value
          .filter(item => !!item)
          .map(item => ({
            isFoundingLocation: false,
            isHeadquarter: false,
            locationId: item.id,
          })),
      };
    case 'officeVideo':
      return { officeVideo: value.rawUrl ? value.rawUrl : null };
    case 'officeImages':
      return { officeImageIds: [...value.filter(item => !!item).map(item => idify(item))] };
    case 'benefits':
      return { benefitIds: [...value.filter(item => !!item).map(item => item.value)] };
    case 'clients':
      return { clientLogoIds: [...value.filter(item => !!item).map(item => get(item, 'logo.id', null))] };
    case 'marketAreas':
      return { marketAreaIds: [...value.filter(item => !!item).map(item => item.id)] };
    case 'marketCountries':
      return { marketCountryIds: [...value.filter(item => !!item).map(item => item.id)] };
    case 'media':
      return { mediaIds: [...value.filter(item => !!item).map(item => idify(item))] };
    case 'socialMedia':
      return { socialMedia: [...value.filter(item => !!item).map(item => pick(item, ['url', 'source']))] };
    case 'funding':
      return { fundingId: value && value.id ? value.id : null };
    case 'latestFunding':
      return {
        latestFunding: {
          currencyId: value.currency ? value.currency.id : DEFAULT_CURRENCY_ID,
          fundedAt: value.fundedAt ? new Date(value.fundedAt).toISOString() : undefined,
          round: value.round,
          value: parseInt(value.value),
        },
      };
  }
}

@connect(
  state => ({
    recruiter: getRecruiter(state),
  }),
  { replace, setErrorFlashMessage, setSuccessFlashMessage, updateCompanyProfile, updateCompanyProfileId },
)
export default class CompanyForm extends PureComponent {
  static propTypes = {
    companyProfileId: PropTypes.string,
    recruiter: PropTypes.object.isRequired,
    replace: PropTypes.func.isRequired,
    setErrorFlashMessage: PropTypes.func.isRequired,
    setSuccessFlashMessage: PropTypes.func.isRequired,
    updateCompanyProfile: PropTypes.func.isRequired,
    updateCompanyProfileId: PropTypes.func.isRequired,
  };

  state = {
    changes: undefined,
    company: undefined,
    data: undefined,
    errors: {},
    publishedProfileId: undefined,
  };

  async componentDidMount() {
    const { companyProfileId, setErrorFlashMessage, replace, companyProfileId } = this.props;

    try {
      const { data } = await apolloClient.query<CompanyByProfileQuery, CompanyByProfileQueryVariables>({
        fetchPolicy: 'no-cache',
        query: CompanyByProfileDocument,
        variables: { companyProfileId },
      });

      this.setState({
        company: data.company,
        profile: data.company.profile,
        publishedProfileId: data.company.profile.id,
      });
    } catch (error) {
      if (isErrorWithCode(error, ApolloErrorCode.RESOURCE_NOT_FOUND)) {
        try {
          const { data } = await apolloClient.query({
            fetchPolicy: 'no-cache',
            query: CompanyDocument,
            variables: { companyProfileState: CompanyProfileStateEnum.DRAFT },
          });

          this.setState({
            company: data.company,
            profile: data.company.profile,
            publishedProfileId: data.company.profile.id,
          });
          replace(
            createLink({
              link: COMPANY_PROFILE_ROUTE,
              params: { companyProfileId: data.company.profile.id },
            }),
          );
        } catch (error) {
          if (isErrorWithCode(error, ApolloErrorCode.RESOURCE_NOT_FOUND)) {
            try {
              const { data } = await apolloClient.query({
                fetchPolicy: 'no-cache',
                query: CompanyDocument,
                variables: { companyProfileState: CompanyProfileStateEnum.PUBLISHED },
              });

              this.setState({
                company: data.company,
                profile: data.company.profile,
                publishedProfileId: data.company.profile.id,
              });
              replace(
                createLink({
                  link: COMPANY_PROFILE_ROUTE,
                  params: { companyProfileId: data.company.profile.id },
                }),
              );
            } catch (error) {
              captureError(error);
              setErrorFlashMessage('Company profile not found');
            }
          } else {
            captureError(error);
            setErrorFlashMessage('Company profile not found');
          }
        }
      } else {
        captureError(error);
        setErrorFlashMessage('Company profile not found');
      }
    }
  }

  onFieldChange = (key, value) => {
    this.setState(applyToData(key, value, { [key]: value }), this.saveForm);
  };

  onLinkChange = (key, value) => {
    const { profile } = this.state;
    const link = find(profile.socialMedia, { source: key });
    const newLink = { source: key, url: value };

    if (link) {
      let newList = reject(profile.socialMedia, { source: key });

      if (value) {
        newList = [...newList, newLink];
      }

      return this.setState(applyToData('socialMedia', newList, { socialMedia: newList }), this.saveForm);
    }

    return this.setState(
      applyToData('socialMedia', [...profile.socialMedia, newLink], { socialMedia: [...profile.socialMedia, newLink] }),
      this.saveForm,
    );
  };

  publishProfile = async () => {
    const { profile } = this.state;
    const { setErrorFlashMessage, setSuccessFlashMessage, updateCompanyProfile } = this.props;

    const errors = validateForm(profile);

    if (!isEqual(errors, {})) {
      this.setState({ errors });
    } else {
      this.setState({ errors: {}, publishingForm: true });

      try {
        const {
          data: { companyProfile },
        } = await apolloClient.mutate({
          mutation: PublishCompanyProfileForPublicProfileDocument,
          variables: { profileId: profile.id },
        });

        await apolloClient.query({
          fetchPolicy: 'network-only',
          query: SessionDocument,
        });

        // After company publish sync company logo and name in left menu
        updateCompanyProfile(companyProfile);

        this.setState(({ profile }) => {
          return {
            errors: {},
            profile: {
              ...profile,
              ...pick(companyProfile, ['isComplete', 'isPublished']),
            },
            publishedProfileId: profile.id,
            publishingForm: false,
          };
        });

        setSuccessFlashMessage('Company profile was successfully updated');
      } catch (error) {
        if (error.graphQLErrors) {
          error.graphQLErrors.forEach(graphQLError => {
            setErrorFlashMessage(get(graphQLError, 'message'));
          });
        }

        this.setState({ errors: 'Something went wrong', publishingForm: false });
      }
    }
  };

  componentDidUpdate(_, prevState) {
    const { profile } = this.state;

    if (profile && prevState.profile && profile.id !== prevState.profile.id) {
      // Update company profile id in redux, to keep company tab active in left menu.
      this.props.updateCompanyProfileId(profile.id);
      this.props.replace(
        createLink({
          link: COMPANY_PROFILE_ROUTE,
          params: { companyProfileId: profile.id },
        }),
      );
    }
  }

  componentWillUnmount() {
    const { profile, publishedProfileId } = this.state;
    // Change company profile ID back to published one, If we move to another page without publishing company profile.

    if (profile && profile.id !== publishedProfileId) {
      this.props.updateCompanyProfileId(publishedProfileId);
    }
  }

  saveForm = debounce(async () => {
    try {
      const {
        profile: { id },
        changes,
      } = this.state;

      // Reset changes
      this.setState({ changes: undefined });

      const {
        data: { saveCompanyProfile },
      } = await apolloClient.mutate({
        mutation: SaveCompanyProfileDocument,
        variables: { changes, profileId: id },
      });

      this.setState(({ profile }) => ({ profile: { ...profile, ...pick(saveCompanyProfile, FIELD_UPDATE_LIST) } }));
    } catch (error) {
      if (error.graphQLErrors) {
        error.graphQLErrors.forEach(graphQLError => {
          this.props.setErrorFlashMessage(get(graphQLError, 'message'));
        });
      }
    }
  }, 400);

  setImage = (key, image) => {
    this.setState(applyToData(key, image, { [key]: image }), this.saveForm);
  };

  setGalleryImage = (key, image) => {
    const { profile } = this.state;
    this.setState(applyToData(key, [...profile[key], image], { [key]: [...profile[key], image] }), () => {
      this.saveForm();
      trackUpdateCompanyProfile({
        [COMPANY_PROFILE_DICTIONARY[key]]: map(this.state.profile[key], 'fullUrl'),
      });
    });
  };

  onGalleryImageRemove = (key, image) => {
    const { profile } = this.state;
    this.setState(
      applyToData(key, reject(profile[key], { id: image.id }), { [key]: reject(profile[key], { id: image.id }) }),
      () => {
        this.saveForm();
        trackUpdateCompanyProfile({
          [COMPANY_PROFILE_DICTIONARY[key]]: map(this.state.profile[key], 'fullUrl'),
        });
      },
    );
  };

  setContentRef = contentRef => {
    if (!this.state.contentRef) {
      this.setState({ contentRef });
    }
  };

  saveCompany = () => {
    const { company } = this.state;

    return apolloClient
      .mutate({
        mutation: UpdateCompanyDocument,
        variables: {
          companyId: company?.id,
          updateInput: {
            companyTypeIds: company?.companyTypes.map(companyType => companyType.id),
            industryIds: company?.industries.map(industry => industry.id || industry._id),
          },
        },
      })
      .then(() => {
        this.setState(({ profile }) => ({ ...profile, isPublished: false }));
      });
  };

  onCompanyFieldChange = (key, value) => {
    this.setState(({ company }) => ({ company: { ...company, [key]: value } }), this.saveCompany);
  };

  setScroll = element => element.scrollIntoView({ behavior: 'smooth', block: 'start' });

  render() {
    const { recruiter } = this.props;
    const { company, errors, profile, publishingForm, contentRef } = this.state;

    if (!profile || !company) {
      return null;
    }

    if (errors.load) {
      return <LoadError error={errors.load} />;
    }

    return (
      <Query<CompanyPublicProfileListDataQuery> query={CompanyPublicProfileListDataDocument}>
        {({ data: { companyBenefits, companyTypes, companySizes, companyFundings, companyRevenues } = {} }) => (
          <View
            benefits={companyBenefits}
            company={company}
            companyFundings={companyFundings?.filter(
              (revenue): revenue is Pick<CompanyFundingType, 'id' | 'key' | 'max' | 'min' | 'value'> => !!revenue,
            )}
            companyRevenues={companyRevenues?.filter(
              (revenue): revenue is Pick<CompanyRevenueType, 'id' | 'key' | 'value' | 'max' | 'min'> => !!revenue,
            )}
            companySizes={companySizes?.filter(
              (size): size is Pick<CompanySizeType, 'id' | 'name' | 'min' | 'max'> => !!size,
            )}
            companyTypes={companyTypes?.filter((type): type is Pick<CompanyTypeType, 'id' | 'key' | 'name'> => !!type)}
            contentRef={contentRef}
            data={profile}
            errors={errors}
            minMissionLength={MISSION_STATEMENT_MIN_LENGTH}
            onCompanyFieldChange={this.onCompanyFieldChange}
            onFieldChange={this.onFieldChange}
            onGalleryImageRemove={this.onGalleryImageRemove}
            onLinkChange={this.onLinkChange}
            publishProfile={this.publishProfile}
            isPublishing={publishingForm}
            recruiter={recruiter}
            setContentRef={this.setContentRef}
            setGalleryImage={this.setGalleryImage}
            setImage={this.setImage}
            setScroll={this.setScroll}
          />
        )}
      </Query>
    );
  }
}
